import { createTheme, StyledEngineProvider, ThemeProvider } from '@mui/material';
import AppRouter from './router/AppRouter';
import { useLocation } from 'react-router-dom';
import AuthRouter from './router/AuthRouter';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { Toaster } from "react-hot-toast";
import { useEffect, useMemo, useState } from 'react';
import SplashScreen from "./Pages/SplashScreen"
import { useIsLogin } from './hooks/useIsLogin';
import { Navigate } from 'react-router-dom';

import { messaging } from './components/firebase';
import { getToken } from 'firebase/messaging';
import i18n from "./i18n";
import { useTranslation } from 'react-i18next';

function App() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState(i18n.language);
  const [isLoading, setIsLoading] = useState(true);
  const [loading, isLogin] = useIsLogin();  
  const direction = i18n.language === "fa" ? "rtl" : "ltr";

  // Memoize emotion cache for RTL or LTR
  const emotionCache = useMemo(() =>
    createCache({
      key: direction === 'rtl' ? 'muirtl' : 'mui',
      stylisPlugins: direction === 'rtl' ? [prefixer, rtlPlugin] : [],
    }), [direction]);

  const theme = createTheme({
    direction,
    palette: {
      primary: { main: "#512C63" }, // purple
      secondary: { main: "#00735D" }, // teal
      error: { main: "#C81D11" }, // red
      warning: { main: "#f57c00" }, // orange
      info: { main: "#0C72D7" }, // blue
      success: { main: "#008A70" }, // green
      grey: { 500: "#BAB3BD" },
      text: {
        primary: "#191923",
        secondary: "#929292",
      },
    },
    typography: {
      fontFamily: i18n.language === "fa" ? "Vazirmatn FD" : "sans-serif",
      h1: { fontWeight: 600, fontSize: "22px", lineHeight: "27px", letterSpacing: "0" },
      h2: { fontWeight: 600, fontSize: "20px", lineHeight: "27px", letterSpacing: "0" },
      h3: { fontWeight: 600, fontSize: "18px", lineHeight: "27px", letterSpacing: "0" },
      h4: { fontWeight: 600, fontSize: "16px", lineHeight: "27px", letterSpacing: "0" },
      h5: { fontWeight: 400, fontSize: "14px", lineHeight: "25px", letterSpacing: "0" },
      subtitle1: { fontWeight: 500, fontSize: "12px", lineHeight: 1, letterSpacing: "0" },
      overline: { fontWeight: 400, fontSize: "10px", lineHeight: 1.5, letterSpacing: "0" },
    },
  });
  
  useEffect(() => {
    document.dir = direction; // update the HTML document direction dynamically
  }, [direction]);

  useEffect(() => {
    const requestNotificationPermission = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          return;
        }
  
        const registration = await navigator.serviceWorker.register('/service-worker.js');
        await navigator.serviceWorker.ready;
  
         await getToken(messaging, {
          vapidKey: 'BAcTjxVeyGdptGHlBQJhdSwydq6yKpwNjsoQLWBAdPbnBXSvleUmXEAq2O8pusHxK7iWBTW1dXQgNlrXb55LcwY',
          serviceWorkerRegistration: registration
        });
      } catch (error) {
        console.error('Notification setup failed:', error);
      }
    };
  
    requestNotificationPermission();
  }, []);

  useEffect(() => {
    const onLanguageChanged = (lng) => setCurrentLang(lng);
    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, [i18n]);

  return (
    <CacheProvider value={emotionCache}>   
    <StyledEngineProvider>
      <ThemeProvider theme={theme}>
        {loading || isLoading ? (
          <SplashScreen onComplete={() => setIsLoading(false)} />
        ) : (
          !isLogin && !location.pathname.includes("/auth") ? (
            <Navigate to="/auth/login" replace />
          ) : (
            location.pathname.includes("/auth") ? <AuthRouter /> : <AppRouter />
          )
        )}
        <Toaster position='top-left' />
      </ThemeProvider>
    </StyledEngineProvider>
  </CacheProvider>   
  );
}

export default App;
